<script setup>
import { computed } from 'vue';
import { usePage, useForm } from '@inertiajs/vue3';
import Button from '@/Components/Button.vue';
import TextInput from '@/Components/TextInput.vue';
import Google from '@/Components/Icons/Google.vue';
import Alert from '@/Components/Alert.vue';

const page = usePage();
const isAuthenticated = computed(() => page.props.auth.user);
const promoBanner = computed(() => page.props.promoBanner || { enabled: false, coupon_code: 'QGMECS' });

const form = useForm({
    email: '',
    password: '',
    password_confirmation: '',
    terms: false,
});

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('register'), {
        onFinish: () => {
            form.reset('password');
            form.reset('password_confirmation');
        },
    });
};
</script>

<template>
    <section v-if="!isAuthenticated" class="py-20 lg:py-32" style="background-color: #F0F5FB;">
        <div class="container mx-auto px-4">
            <div class="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <!-- Left Column - Image -->
                <div class="relative" data-aos="fade-right">
                    <img
                        src="/images/home/couple.png"
                        alt="Couple"
                        class="w-full h-auto"
                    />
                </div>

                <!-- Right Column - Registration Form -->
                <div data-aos="fade-left">
                    <div class="bg-white rounded-3xl overflow-hidden shadow-lg">
                        <!-- Form Header -->
                        <div class="bg-gradient-to-r from-secondary to-primary p-8 text-white">
                            <h2 class="text-2xl lg:text-3xl font-bold mb-3 leading-tight">
                                Profitez de 6 mois gratuits en vous inscrivant.
                            </h2>
                            <p class="text-base">
                                Prêt à vivre de nouvelles expériences ? Rejoins-nous et bénéficie de 6 mois d'abonnement offerts avec le code promo <strong class="font-bold">{{ promoBanner.coupon_code }}</strong> !
                            </p>
                        </div>

                        <!-- Form Body -->
                        <div class="p-8">
                            <form @submit.prevent="submit">
                                <transition name="fade">
                                    <Alert
                                        variant="error"
                                        title="Ooops!"
                                        class="mt-2 mb-6"
                                        v-if="Object.keys(form.errors).length > 0"
                                    >
                                        <template v-for="error in form.errors" :key="error">
                                            <div>{{ error }}</div>
                                        </template>
                                    </Alert>
                                </transition>

                                <div class="input-group">
                                    <TextInput
                                        label="Adresse email"
                                        icon="mail"
                                        required
                                        container-class="w-full"
                                        v-model="form.email"
                                        placeholder="Adresse email"
                                        autocomplete="email"
                                        name="email"
                                    />
                                </div>

                                <div class="input-group">
                                    <TextInput
                                        label="Mot de passe"
                                        icon="lock"
                                        required
                                        container-class="w-full"
                                        v-model="form.password"
                                        placeholder="Mot de passe"
                                        type="password"
                                        autocomplete="current-password"
                                        name="password"
                                    />
                                </div>

                                <div class="input-group">
                                    <TextInput
                                        label="Confirmer le mot de passe"
                                        icon="lock"
                                        container-class="w-full"
                                        v-model="form.password_confirmation"
                                        placeholder="Confirmer le mot de passe"
                                        type="password"
                                        required
                                        autocomplete="current-password"
                                        name="password_confirmation"
                                    />
                                </div>

                                <Button
                                    class="w-full"
                                    type="submit"
                                    :class="{ 'opacity-25': form.processing }"
                                    :disabled="form.processing"
                                >
                                    S'inscrire
                                </Button>

                                <div class="flex items-center justify-between my-6">
                                    <span class="w-1/5 border-b border-dark/4 lg:w-1/4"></span>
                                    <p class="text-xs text-center text-gray-500 uppercase">ou</p>
                                    <span class="w-1/5 border-b border-dark/4 lg:w-1/4"></span>
                                </div>

                                <div class="input-group">
                                    <a :href="route('auth.google')">
                                        <Button
                                            type="button"
                                            variant="light"
                                            class="w-full"
                                        >
                                            <span class="flex items-center gap-2 justify-center">
                                                <Google />
                                                <span class="relative top-0.5">
                                                    S'inscrire avec Google
                                                </span>
                                            </span>
                                        </Button>
                                    </a>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
